// AUTO-GEGENEREERD door scratchpad/emit-data.js — niet met de hand bewerken.
// Bron: repo-eigen sefstars.txt (Swiss Ephemeris), gematcht + gekruiscontroleerd
// tegen de eclipticale lengtes in hd-fixed-stars.metadata.ts (66/66, 0 mismatches).
// RA/Dec in graden (ICRS/J2000). fov = beeldveld in graden. Zie de spec
// docs/superpowers/specs/2026-09-23-hd-fixed-star-sky-charts-design.md.

export type HdSkyKind = 'star' | 'cluster' | 'nebula' | 'dual';

export interface HdFixedStarSkyPosition {
  readonly ra: number;
  readonly dec: number;
  /** IAU 3-letter sterrenbeeld-afkorting (key in constellations.lines.json + HD_CONSTELLATION_NAMES). */
  readonly con: string;
  readonly mag: number;
  readonly bayer: string;
  /** Beeldveld in graden voor deze kaart. */
  readonly fov: number;
}

export interface HdFixedStarSkyEntry extends HdFixedStarSkyPosition {
  readonly kind: HdSkyKind;
  /** Tweede positie voor een dubbelster met dezelfde naam (bv. Gienah in Cygnus). */
  readonly second?: HdFixedStarSkyPosition;
}

/** Per side-nummer (1..66) de sterrenkaart-positie + framing. */
export const HD_FIXED_STAR_SKY: Readonly<Record<number, HdFixedStarSkyEntry>> = {
  1: { ra:10.8974, dec:-17.9866, con:'Cet', mag:2.01, bayer:'beCet', fov:34, kind:'star' },
  2: { ra:56.8712, dec:24.1051, con:'Tau', mag:2.87, bayer:'etTau', fov:3.8, kind:'cluster' },
  3: { ra:64.9483, dec:15.6276, con:'Tau', mag:3.65, bayer:'gaTau', fov:7, kind:'cluster' },
  4: { ra:68.9802, dec:16.5093, con:'Tau', mag:0.86, bayer:'alTau', fov:34, kind:'star' },
  5: { ra:78.6345, dec:-8.2016, con:'Ori', mag:0.13, bayer:'beOri', fov:34, kind:'star' },
  6: { ra:247.3519, dec:-26.432, con:'Sco', mag:0.91, bayer:'alSco', fov:34, kind:'star' },
  7: { ra:262.6082, dec:52.3014, con:'Dra', mag:2.81, bayer:'beDra', fov:34, kind:'star' },
  8: { ra:279.2347, dec:38.7837, con:'Lyr', mag:0.03, bayer:'alLyr', fov:17, kind:'star' },
  9: { ra:297.6958, dec:8.8683, con:'Aql', mag:0.76, bayer:'alAql', fov:34, kind:'star' },
  10: { ra:344.4127, dec:-29.6222, con:'PsA', mag:1.16, bayer:'alPsA', fov:34, kind:'star' },
  11: { ra:310.358, dec:45.2803, con:'Cyg', mag:1.25, bayer:'alCyg', fov:34, kind:'star' },
  12: { ra:81.2828, dec:6.3497, con:'Ori', mag:1.64, bayer:'gaOri', fov:34, kind:'star' },
  13: { ra:79.1723, dec:45.998, con:'Aur', mag:0.08, bayer:'alAur', fov:34, kind:'star' },
  14: { ra:81.573, dec:28.6075, con:'Tau', mag:1.65, bayer:'beTau', fov:34, kind:'star' },
  15: { ra:37.9546, dec:89.2641, con:'UMi', mag:2.02, bayer:'alUMi', fov:34, kind:'star' },
  16: { ra:37.9546, dec:89.2641, con:'UMi', mag:2.02, bayer:'alUMi', fov:34, kind:'star' },
  17: { ra:88.7929, dec:7.4071, con:'Ori', mag:0.42, bayer:'alOri', fov:34, kind:'star' },
  18: { ra:22.8709, dec:15.3458, con:'Psc', mag:3.62, bayer:'etPsc', fov:34, kind:'star' },
  19: { ra:95.988, dec:-52.6957, con:'Car', mag:-0.74, bayer:'alCar', fov:44, kind:'star' },
  20: { ra:113.6495, dec:31.8883, con:'Gem', mag:1.58, bayer:'alGem', fov:34, kind:'star' },
  21: { ra:116.329, dec:28.0262, con:'Gem', mag:1.14, bayer:'beGem', fov:34, kind:'star' },
  22: { ra:114.8255, dec:5.225, con:'CMi', mag:0.37, bayer:'alCMi', fov:11, kind:'star' },
  23: { ra:236.067, dec:6.4256, con:'Ser', mag:2.63, bayer:'alSer', fov:29, kind:'star' },
  24: { ra:146.4628, dec:23.7743, con:'Leo', mag:2.98, bayer:'epLeo', fov:34, kind:'star' },
  25: { ra:152.093, dec:11.9672, con:'Leo', mag:1.4, bayer:'alLeo', fov:34, kind:'star' },
  26: { ra:200.9814, dec:54.9254, con:'UMa', mag:2.27, bayer:'zeUMa', fov:34, kind:'star' },
  27: { ra:177.2649, dec:14.5721, con:'Leo', mag:2.13, bayer:'beLeo', fov:34, kind:'star' },
  28: { ra:140.5284, dec:-55.0107, con:'Vel', mag:2.473, bayer:'kaVel', fov:34, kind:'star' },
  29: { ra:187.4661, dec:-16.5154, con:'Crv', mag:2.94, bayer:'deCrv', fov:21, kind:'star' },
  30: { ra:201.2982, dec:-11.1613, con:'Vir', mag:0.97, bayer:'alVir', fov:34, kind:'star' },
  31: { ra:286.3525, dec:13.8635, con:'Aql', mag:2.99, bayer:'zeAql', fov:34, kind:'star' },
  32: { ra:31.7934, dec:23.4624, con:'Ari', mag:2.01, bayer:'alAri', fov:23, kind:'star' },
  33: { ra:326.7602, dec:-16.1273, con:'Cap', mag:2.83, bayer:'deCap', fov:34, kind:'star' },
  34: { ra:213.9153, dec:19.1824, con:'Boo', mag:-0.05, bayer:'alBoo', fov:34, kind:'star' },
  35: { ra:263.7336, dec:12.56, con:'Oph', mag:2.07, bayer:'alOph', fov:34, kind:'star' },
  36: { ra:263.4022, dec:-37.1038, con:'Sco', mag:1.62, bayer:'laSco', fov:34, kind:'star' },
  37: { ra:273.4409, dec:-21.0588, con:'Sgr', mag:3.85, bayer:'muSgr', fov:34, kind:'star' },
  38: { ra:17.433, dec:35.6206, con:'And', mag:2.05, bayer:'beAnd', fov:34, kind:'star' },
  39: { ra:10.8974, dec:-17.9866, con:'Cet', mag:2.01, bayer:'beCet', fov:34, kind:'star' },
  40: { ra:210.9559, dec:-60.373, con:'Cen', mag:0.6, bayer:'beCen', fov:34, kind:'star' },
  41: { ra:271.1292, dec:-24.3583, con:'Sgr', mag:4.6, bayer:'NGC6530', fov:6, kind:'nebula' },
  42: { ra:134.8019, dec:48.0418, con:'UMa', mag:3.14, bayer:'ioUMa', fov:34, kind:'star' },
  43: { ra:30.9748, dec:42.3297, con:'And', mag:2.1, bayer:'ga-1And', fov:34, kind:'star' },
  44: { ra:55.8121, dec:-9.7634, con:'Eri', mag:3.54, bayer:'deEri', fov:34, kind:'star' },
  45: { ra:47.0422, dec:40.9556, con:'Per', mag:2.12, bayer:'bePer', fov:34, kind:'star' },
  46: { ra:141.8968, dec:-8.6586, con:'Hya', mag:1.97, bayer:'alHya', fov:34, kind:'star' },
  47: { ra:151.8331, dec:16.7627, con:'Leo', mag:3.41, bayer:'etLeo', fov:34, kind:'star' },
  48: { ra:206.8852, dec:49.3133, con:'UMa', mag:1.86, bayer:'etUMa', fov:34, kind:'star' },
  49: { ra:258.6619, dec:14.3903, con:'Her', mag:3.06, bayer:'alHer', fov:34, kind:'star' },
  50: { ra:2.0969, dec:29.0904, con:'And', mag:2.06, bayer:'alAnd', fov:34, kind:'star' },
  51: { ra:3.309, dec:15.1836, con:'Peg', mag:2.84, bayer:'gaPeg', fov:34, kind:'star' },
  52: { ra:84.9123, dec:-34.0741, con:'Col', mag:2.65, bayer:'alCol', fov:21, kind:'star' },
  53: { ra:233.672, dec:26.7147, con:'CrB', mag:2.24, bayer:'alCrB', fov:21, kind:'star' },
  54: { ra:134.6218, dec:11.8577, con:'Cnc', mag:4.249, bayer:'alCnc', fov:34, kind:'star' },
  55: { ra:165.4603, dec:56.3824, con:'UMa', mag:2.37, bayer:'beUMa', fov:34, kind:'star' },
  56: { ra:146.4628, dec:23.7743, con:'Leo', mag:2.98, bayer:'epLeo', fov:34, kind:'star' },
  57: { ra:211.0973, dec:64.3759, con:'Dra', mag:3.68, bayer:'alDra', fov:34, kind:'star' },
  58: { ra:298.9598, dec:-26.2995, con:'Sgr', mag:4.7, bayer:'omeSgr', fov:34, kind:'star' },
  59: { ra:311.919, dec:-9.4958, con:'Aqr', mag:3.77, bayer:'epAqr', fov:34, kind:'star' },
  60: { ra:325.0227, dec:-16.6623, con:'Cap', mag:3.67, bayer:'gaCap', fov:34, kind:'star' },
  61: { ra:183.9515, dec:-17.5419, con:'Crv', mag:2.58, bayer:'gaCrv', fov:16, kind:'dual', second:{ra:311.5535,dec:33.9703,con:'Cyg',mag:2.48,bayer:'epCyg',fov:34} },
  62: { ra:322.8897, dec:-5.5712, con:'Aqr', mag:2.89, bayer:'beAqr', fov:34, kind:'star' },
  63: { ra:271.452, dec:-30.4241, con:'Sgr', mag:2.99, bayer:'gaSgr', fov:34, kind:'star' },
  64: { ra:222.7196, dec:-16.0418, con:'Lib', mag:2.75, bayer:'al-2Lib', fov:34, kind:'star' },
  65: { ra:247.555, dec:21.4896, con:'Her', mag:2.77, bayer:'beHer', fov:34, kind:'star' },
  66: { ra:24.4285, dec:-57.2368, con:'Eri', mag:0.46, bayer:'alEri', fov:34, kind:'star' },
};

/** IAU-afkorting -> Latijnse sterrenbeeldnaam (alleen de sterrenbeelden die in de 66 voorkomen). */
export const HD_CONSTELLATION_NAMES: Readonly<Record<string, string>> = {
  And: 'Andromeda',
  Aql: 'Aquila',
  Aqr: 'Aquarius',
  Ari: 'Aries',
  Aur: 'Auriga',
  Boo: 'Boötes',
  Cnc: 'Cancer',
  CMi: 'Canis Minor',
  Cap: 'Capricornus',
  Car: 'Carina',
  Cen: 'Centaurus',
  Cet: 'Cetus',
  Col: 'Columba',
  CrB: 'Corona Borealis',
  Crv: 'Corvus',
  Cyg: 'Cygnus',
  Dra: 'Draco',
  Eri: 'Eridanus',
  Gem: 'Gemini',
  Her: 'Hercules',
  Hya: 'Hydra',
  Leo: 'Leo',
  Lib: 'Libra',
  Lyr: 'Lyra',
  Oph: 'Ophiuchus',
  Ori: 'Orion',
  Peg: 'Pegasus',
  Per: 'Perseus',
  Psc: 'Pisces',
  PsA: 'Piscis Austrinus',
  Sco: 'Scorpius',
  Ser: 'Serpens',
  Sgr: 'Sagittarius',
  Tau: 'Taurus',
  UMa: 'Ursa Major',
  UMi: 'Ursa Minor',
  Vel: 'Vela',
  Vir: 'Virgo',
};
